import { Tooltip, Typography } from "@mui/material";

import {
  EVOTRUX_BORDER_GREY,
  EVOTRUX_ERROR_RED,
  EVOTRUX_LINK_BLUE,
} from "constants/index";

import { ADD_PARTNER_COPY } from "./constants";

interface Props {
  onSubmit: VoidFunction;
  inviteSent: boolean;
  hasFailed: boolean;
  isLoading: boolean;
  // A carrier already in the network has nothing to add, so the row reports the
  // relationship instead of offering the action.
  isPartner?: boolean;
}

const PARTNER_PILL_SX = {
  ...{ fontSize: "0.75rem", fontWeight: 500 },
  display: "inline-block",
  px: 0.75,
  py: 0.125,
  borderRadius: 4,
  border: `1px solid ${EVOTRUX_BORDER_GREY}`,
  color: EVOTRUX_LINK_BLUE,
} as const;

const INVITE_SENT_SX = {
  fontSize: "0.75rem",
  fontWeight: 500,
} as const;

// Only the actionable variant renders as a button element, so it is the only one
// with an uppercasing transform and a button chrome reset to undo.
const ACTIONABLE_BASE_SX = {
  ...INVITE_SENT_SX,
  textTransform: "none",
  background: "none",
  border: "none",
  p: 0,
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
} as const;

const ACTIONABLE_SX = {
  ...ACTIONABLE_BASE_SX,
  color: EVOTRUX_LINK_BLUE,
} as const;

const ACTIONABLE_FAILED_SX = {
  ...ACTIONABLE_BASE_SX,
  color: EVOTRUX_ERROR_RED,
} as const;

/**
 * Text-link presentation of the add-partner action, for rows that have no room
 * for a button. Sits alongside other inline text actions, so it matches their
 * type scale and colour rather than introducing a second link style.
 */
export function AddCarrierToNetworkLink(props: Props) {
  const {
    onSubmit,
    inviteSent,
    hasFailed,
    isLoading,
    isPartner = false,
  } = props;

  // Checked before the loading guard: partnership is known from the quote, not
  // from the pending-invite request, so the pill has nothing to wait for.
  if (isPartner) {
    return (
      <Tooltip title="This carrier is in your partner network">
        <Typography variant="body2" sx={PARTNER_PILL_SX}>
          Partner
        </Typography>
      </Tooltip>
    );
  }

  // A settled invite is reported even mid-request, so a click lands on text that
  // changes rather than briefly on nothing. Only the state before the pending
  // invites are known renders nothing, and callers hold this back until then:
  // inside a row of links, an empty child still earns a divider.
  if (isLoading && !inviteSent) {
    return null;
  }

  if (inviteSent) {
    return (
      <Tooltip title={ADD_PARTNER_COPY.inviteSent.tooltip}>
        <Typography variant="body2" color="text.secondary" sx={INVITE_SENT_SX}>
          {ADD_PARTNER_COPY.inviteSent.label}
        </Typography>
      </Tooltip>
    );
  }

  const copy = hasFailed
    ? ADD_PARTNER_COPY.failed
    : ADD_PARTNER_COPY.actionable;

  return (
    <Tooltip title={copy.tooltip}>
      <Typography
        component="button"
        onClick={onSubmit}
        sx={hasFailed ? ACTIONABLE_FAILED_SX : ACTIONABLE_SX}
      >
        {copy.label}
      </Typography>
    </Tooltip>
  );
}
